import { useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { CaseType, ICase } from '@brands/services/cases/types/ICase';
import { selectAuth } from '@brands/store/selectors/auth';
import { isStaffCaseCreationRole } from '@brands/Utils/reviewProfileBasePath';
import { selectCurrentPatientState } from '@brands/store/selectors/currentPatientState';
import { getFriendlyNameOfConsultationType } from '@brands/Utils/getFriendlyName';
import { convertZoneAbbr, getTimezoneAbbreviation } from '@brands/Utils/getParsedTimezoneName';
import CloseIcon from '@mui/icons-material/Close';
import { Button } from '@mui/material';
import { AddToCalendarButton } from 'add-to-calendar-button-react';
import moment from 'moment-timezone';
import React, { useCallback, useEffect } from 'react';

import styles from './dashboardModal.module.scss';

type ScheduleLiveConsultationType = {
  newCase: ICase;
  isRescheduled?: boolean;
};

const ScheduleLiveConsultation: React.FC<ScheduleLiveConsultationType> = ({ newCase, isRescheduled }) => {
  const roleNavigate = useRoleNavigate();
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  const currentState: string = useAppSelector(selectCurrentPatientState);
  const { userInfo } = useAppSelector(selectAuth);
  const currentTimezone = isStaffCaseCreationRole(userInfo.role.name)
    ? getTimezoneAbbreviation(newCase.patient.address?.state as string) || getTimezoneAbbreviation(currentState[userInfo.id])
    : getTimezoneAbbreviation(currentState[userInfo.id]);
  const timezoneWithUnderscore = convertZoneAbbr(currentTimezone as string) || timeZone;
  const goToDashboard = useCallback(() => {
    if (isStaffCaseCreationRole(userInfo.role.name)) {
      roleNavigate('/dashboard');
    } else if (isRescheduled) {
      roleNavigate('/my-care');
    } else {
      roleNavigate('/dashboard');
    }
  }, [userInfo.role.name, roleNavigate, isRescheduled]);

  const getCaseDate = (): string => {
    const date = moment.utc(newCase.video_conference_date as string)?.tz(timezoneWithUnderscore);
    if (date?.isValid()) {
      return date.format('dddd, MMMM D, YYYY');
    }

    return '';
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .atcb-button {
        background: none !important;
        border: none !important;
        box-shadow: none !important;
        color: inherit !important;
      }
      .atcb-button svg {
        display: none !important;
      }
      .atcb-button.custom {
        text-transform: lowercase !important;
      }
    `;
    document.head.appendChild(style);
  }, []);

  const getCaseTime = (): string => {
    const date = moment.utc(newCase.video_conference_date as string).tz(timezoneWithUnderscore);
    if (date.isValid()) {
      return date.format('h:mm A z');
    }

    return '';
  };

  return (
    <div className={styles.modalBackground} onClick={() => goToDashboard()}>
      <div
        className={`${styles.modalContainer} ${styles.scheduleLiveConsultation} ${styles.customizedScheduleLiveConsultation} fs-unmask`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.modalCloseIcon}>
          <button type="button" onClick={() => goToDashboard()} aria-label="Close modal">
            <CloseIcon />
          </button>
        </div>
        <div className={`${styles.body} ${styles.bodyWrap}`}>
          <span className={styles.bodyHeader} data-testid="schedule-live-consultation-header">
            Thank you!
          </span>
          <div className={styles.doctorDetails}>
            <span className={styles.notify}>
              {`Your ${
                newCase.type !== CaseType.oral_health_coaching
                  ? 'dentist consultation'
                  : 'oral health coaching appointment'
              } is ${isRescheduled ? 'rescheduled' : 'scheduled'} for `}
              <span className={styles.notifyDate}>
                {getCaseDate()} at {getCaseTime()}
              </span>
              .{' '}
              <span className={`${styles.appointmentAddToCalendarButton} fs-unmask`}>
                <AddToCalendarButton
                  name={`Dentistry.one ${getFriendlyNameOfConsultationType(newCase.type, false)}`}
                  options={['Google', 'iCal', 'Outlook.com']}
                  description={`${getFriendlyNameOfConsultationType(newCase.type, false)}`}
                  location="World Wide Web"
                  startDate={moment.utc(newCase.video_conference_date).tz(timeZone).format('YYYY-MM-DD')}
                  endDate={moment.utc(newCase.video_conference_date).tz(timeZone).format('YYYY-MM-DD')}
                  startTime={moment.utc(newCase.video_conference_date).tz(timeZone).format('HH:mm')}
                  endTime={moment.utc(newCase.video_conference_date).tz(timeZone).add(1, 'hour').format('HH:mm')}
                  useUserTZ
                  timeZone={timeZone}
                  hideIconButton
                  label="Add to calendar."
                  trigger="click"
                  customCss="/styles.css"
                  buttonStyle="custom"
                />
              </span>
            </span>
          </div>
          <div className={styles.btnContainer}>
            <Button variant="contained" onClick={() => goToDashboard()}>
              Return to Dashboard
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

ScheduleLiveConsultation.defaultProps = {
  isRescheduled: false,
};

export default ScheduleLiveConsultation;
